import type { Country } from '@seek/melways-sites';

import type { ApplicationAttachmentType } from 'src/App/AppPageBlock/graphql/hirer-types.generated';
import type { AttachmentTypeName } from 'src/constants/attachmentTypes';
import type {
  MUST_HAVE_TOGGLE_TYPE,
  TOGGLE_STATE_OFF,
  TOGGLE_STATE_ON,
} from 'src/constants/toggle';

import {
  type EVENT_NAME_CLEAR_FILTERS_BUTTON_PRESSED,
  type EVENT_NAME_FILTERS_INTERACTED,
  EVENT_NAME_DOWNLOAD_DOCUMENT,
  EVENT_NAME_EXPAND_DOCUMENT,
  EVENT_NAME_COLLAPSE_DOCUMENT,
  EVENT_NAME_CANDIDATE_DETAILS_EMAIL_PRESSED,
  EVENT_NAME_PAGE_LOADED,
  EVENT_NAME_CANDIDATE_DETAILS_PHONE_TEL_LINK_PRESSED,
  EVENT_NAME_SELECT_CANDIDATE,
  EVENT_NAME_CANDIDATE_LIST_SORT_OPTION_PRESSED,
  EVENT_NAME_CANDIDATE_JOB_STATUS_CHANGED_SUCCEEDED,
  TRACKING_PAGE_DATA_HOME,
  EVENT_NAME_DELETE_NOTE_PRESSED,
  EVENT_NAME_NOTES_TAB_PRESSED,
  EVENT_NAME_ADD_NOTE_PRESSED,
  EVENT_NAME_NOTES_IN_CANDIDATE_LIST_PRESSED,
  EVENT_NAME_PROFILE_TAB_PRESSED,
  EVENT_NAME_MORE_QUESTIONS_CANDIDATE_LIST_PRESSED,
  EVENT_NAME_SEND_EMAIL_SUCCEEDED,
  EVENT_NAME_BULK_ACTIONS_SEND_EMAIL_SUCCEEDED,
  EVENT_NAME_EMAIL_TAB_PRESSED,
  EVENT_NAME_EMAIL_CANDIDATE_LIST_PRESSED,
  EVENT_NAME_BULK_ACTIONS_CHANGE_STATUS_SUCCEEDED,
  EVENT_NAME_SEND_EMAIL_PRESSED,
  EVENT_NAME_BULK_ACTIONS_MOVE_TAB_PRESSED,
  EVENT_NAME_JOB_ACTIONS_VIEW_AD_PRESSED,
  EVENT_NAME_JOB_ACTIONS_EDIT_AD_PRESSED,
  EVENT_NAME_JOB_ACTIONS_VIEW_AD_INFORMATION_PRESSED,
  EVENT_NAME_JOB_ACTIONS_REFRESH_PRESSED,
  EVENT_NAME_JOB_ACTIONS_COPY_PRESSED,
  EVENT_NAME_JOB_ACTIONS_EXPIRE_PRESSED,
  EVENT_NAME_CHANGED_RATING,
  EVENT_NAME_CLEARED_RATING,
  EVENT_NAME_TAB_OPT_OUT_PRESSED,
  EVENT_TALENT_SEARCH_BUTTON_LINK_OUT_CLICKED,
  EVENT_NAME_CLICKED_RESUME_TAB,
  EVENT_NAME_CLICKED_COVER_LETTER_TAB,
  EVENT_NAME_SELECTION_CRITERIA_TAB_PRESSED,
  EVENT_NAME_DESKTOP_CLICKED_RESUME_IN_CANDIDATE_LIST,
  EVENT_NAME_DESKTOP_CLICKED_COVER_LETTER_IN_CANDIDATE_LIST,
  EVENT_NAME_DESKTOP_SELECTION_CRITERIA_LIST_PRESSED,
  EVENT_NAME_PREVIOUS_APPLICATIONS_IN_CANDIDATE_LIST_PRESSED,
  EVENT_NAME_RATING_IN_CANDIDATE_LIST_PRESSED,
  EVENT_NAME_CLICKED_PREVIOUS_APPLICATIONS_TAB,
  EVENT_NAME_CANDIDATE_DETAILS_EMAIL_COPY_PRESSED,
  EVENT_NAME_CANDIDATE_DETAILS_PHONE_COPY_PRESSED,
  EVENT_NAME_DOWNLOAD_CANDIDATES_LINK_PRESSED,
  EVENT_NAME_NUMBERED_PAGE_PRESSED,
  EVENT_NAME_CLICKED_NEXT_PAGE_BUTTON,
  EVENT_NAME_CLICKED_PREVIOUS_PAGE_BUTTON,
  EVENT_NAME_SOCIAL_SHARE_FACEBOOK_PRESSED,
  EVENT_NAME_SOCIAL_SHARE_TWITTER_PRESSED,
  EVENT_NAME_SOCIAL_SHARE_LINKEDIN_PRESSED,
  EVENT_NAME_SOCIAL_SHARE_COPY_JOBAD_PRESSED,
  EVENT_NAME_CANDIDATE_DOCUMENTS_DELETE_PRESSED,
  EVENT_NAME_ATTACHMENT_UPLOAD_SUCCEEDED,
  EVENT_NAME_ADD_ATTACHMENT_PRESSED,
  EVENT_NAME_JOB_ACTIONS_NOTIFICATIONS_SELF_PRESSED,
  EVENT_JOB_ACTIONS_TEAM_ACCESS_PRESSED,
  EVENT_NAME_ACTIVITY_LOG_NOTE_PRESSED,
  EVENT_NAME_CLICKED_HIRER_ACTIVITY_LIST_TAB,
  EVENT_NAME_CANDIDATE_LIST_TAB_PRESSED,
  EVENT_NAME_PARENT_FILTER_INTERACTED_ON,
  EVENT_NAME_PARENT_FILTER_OFF_PRESSED,
  EVENT_NAME_ACTIVITY_LOG_LOAD_MORE_PRESSED,
  EVENT_NAME_ACTIVITY_LOG_REFRESH_TO_VIEW_PRESSED,
  EVENT_NAME_MUST_HAVE_FILTER_INTERACTED,
  EVENT_NAME_MATCHED_QUALITIES_VIEW_MORE_PRESSED,
  EVENT_NAME_MATCHED_QUALITIES_VIEW_LESS_PRESSED,
  LINK_NAME_DATA_RETENTION_CANDIDATES_HIDDEN,
  LINK_NAME_DATA_RETENTION_SHOW_CANDIDATES,
  EVENT_NOTIFICATION_DISPLAYED,
  EVENT_NOTIFICATION_PRESSED,
  type TRACKING_CANDIDATE_LIST,
  EVENT_NAME_BULK_ACTIONS_EMAIL_TAB_PRESSED,
  EVENT_NAME_VIEW_APPLICATIONS_AFTER_BULK_MOVE_PRESSED,
  EVENT_NAME_SHOW_MORE_CANDIDATES_IN_BULK_ACTION_PRESSED,
  EVENT_NAME_VIEW_AD_PERFORMANCE_PRESSED,
  EVENT_NAME_PPA_BILLABLE_APPLICANTS_DEFINITION_PRESSED,
  EVENT_NAME_CANDIDATE_CARD_SIZE_CHANGE_PRESSED,
  EVENT_NAME_RECOMMENDED_CANDIDATES_TAB_PRESSED,
  EVENT_CANDIDATE_MANAGEMENT_TALENT_SEARCH_VIEW_RECOMMENDED_CANDIDATES_BTN_PRESSED,
  EVENT_NAME_ALERT_DISMISS_PRESSED,
  EVENT_NAME_SRN_BUTTON_PRESSED,
  BANNER_DISPLAYED,
  EVENT_NAME_FREEMIUM_BUTTON_PRESSED,
  EVENT_NAME_FREEMIUM_BUTTON_DISPLAYED,
} from '../constants/pageTrackingData';
import { trackEvent, trackEventView, trackPageLoad } from '../modules/events';

import type {
  SentEmailSingleAttributes,
  SentEmailMultipleAttributes,
  UpdateStatusMultipleAttributes,
  TrackBulkActionsItemLinkClickedAttributes,
  TrackStatusUpdateAttributes,
} from './eventsTypes';

interface TrackCandidateManagementPageLoadData {
  seekAdvertiserId: string;
  userId: number;
  sortBy: string;
  bucket: string;
  siteLanguage: 'en' | 'th' | 'id' | 'zh';
  zone: string;
  brand: string;
  siteCountry: Country;
  prospectInBucketCount: number;
  seekListingId?: number;
  seekReferenceBalance: number | null;
  isCandidateListFiltered: boolean;
  isSeekReferenceJob: boolean;
  testingId: string;
  adProductName: string;
  candidateCardStatus: 'Expand' | 'Collapse' | 'No RR';
  searchLocationId: string[];
  searchNationalityId: string[];
}

export interface TrackDataRetentionCandidateListData {
  seekAdvertiserId: string;
  userId: number;
  seekListingId: number;
  currentPage: typeof TRACKING_CANDIDATE_LIST;
  bucket: 'inbox';
  listingDate: string;
}

export const trackCandidateManagementPageLoad = ({
  seekAdvertiserId,
  userId,
  sortBy,
  bucket,
  siteCountry,
  siteLanguage,
  prospectInBucketCount,
  seekListingId,
  seekReferenceBalance,
  isCandidateListFiltered,
  isSeekReferenceJob,
  testingId,
  zone,
  brand,
  adProductName,
  candidateCardStatus,
  searchLocationId,
  searchNationalityId,
}: TrackCandidateManagementPageLoadData) =>
  trackPageLoad(
    EVENT_NAME_PAGE_LOADED,
    {
      seekAdvertiserId,
      userId,
      siteCountry,
      siteLanguage,
      adProductName,
      ...TRACKING_PAGE_DATA_HOME,
      seekListingId,
      testingId,
      zone,
      brand,
    },
    {
      sortMode: sortBy,
      bucket,
      prospectInBucketCount,
      isCandidateListFiltered,
      isSeekReferenceJob,
      seekReferenceBalance,
      candidateCardStatus,
      searchLocationId,
      searchNationalityId,
    },
  );

export const mapGraphAttachmentTypesToTrackingAttachmentTypes = (
  attachmentType: ApplicationAttachmentType,
): AttachmentTypeName => {
  switch (attachmentType) {
    case 'RESUME':
      return 'resume';
    case 'COVER_LETTER':
      return 'cover letter';
    case 'SELECTION_CRITERIA':
      return 'selection criteria';
    default:
      return 'hirer uploaded';
  }
};

export const trackAlertDismissEvent = (attributes: {
  alertShownMessageCode: 'Candidate Management - Verified ID Banner';
}) => {
  trackEvent(EVENT_NAME_ALERT_DISMISS_PRESSED, attributes);
};

export const trackSelectCandidateEvent = (attributes: {
  documentTypes: AttachmentTypeName[];
  [x: string]: any;
}) => trackEvent(EVENT_NAME_SELECT_CANDIDATE, attributes);

export const trackDataRetentionCandidatesHiddenDisplayedEvent = (
  attributes: TrackDataRetentionCandidateListData,
) =>
  trackEvent(EVENT_NOTIFICATION_DISPLAYED, {
    ...attributes,
    linkName: LINK_NAME_DATA_RETENTION_CANDIDATES_HIDDEN,
  });
export const trackDataRetentionShowCandidatesPressedEvent = (
  attributes: TrackDataRetentionCandidateListData,
) =>
  trackEvent(EVENT_NOTIFICATION_PRESSED, {
    ...attributes,
    linkName: LINK_NAME_DATA_RETENTION_SHOW_CANDIDATES,
  });

export const trackDownloadDocumentEvent = (attributes: {
  documentType: AttachmentTypeName | undefined;
  [x: string]: any;
}) => trackEvent(EVENT_NAME_DOWNLOAD_DOCUMENT, attributes);

export const trackExpandDocumentEvent = (attributes: {
  documentType: AttachmentTypeName | undefined;
  [x: string]: any;
}) => trackEvent(EVENT_NAME_EXPAND_DOCUMENT, attributes);

export const trackCollapseDocumentEvent = (attributes: {
  documentType: AttachmentTypeName | undefined;
  [x: string]: any;
}) => trackEvent(EVENT_NAME_COLLAPSE_DOCUMENT, attributes);

export const trackClickedResumeTab = (attributes: {
  documentType: AttachmentTypeName | undefined;
  [x: string]: any;
}) => trackEvent(EVENT_NAME_CLICKED_RESUME_TAB, attributes);

export const trackClickedCoverLetterTab = (attributes: {
  documentType: AttachmentTypeName | undefined;
  [x: string]: any;
}) => trackEvent(EVENT_NAME_CLICKED_COVER_LETTER_TAB, attributes);

export const trackClickedSelectionCriteriaTab = (attributes: {
  documentType: AttachmentTypeName | undefined;
  [x: string]: any;
}) => trackEvent(EVENT_NAME_SELECTION_CRITERIA_TAB_PRESSED, attributes);

export const trackUpdateStatusEvent = (
  attributes: TrackStatusUpdateAttributes,
) => trackEvent(EVENT_NAME_CANDIDATE_JOB_STATUS_CHANGED_SUCCEEDED, attributes);

// contact links
export const trackEmailClickEvent = (attributes = {}) =>
  trackEvent(EVENT_NAME_CANDIDATE_DETAILS_EMAIL_PRESSED, attributes);

export const trackPhoneClickEvent = (attributes = {}) =>
  trackEvent(EVENT_NAME_CANDIDATE_DETAILS_PHONE_TEL_LINK_PRESSED, attributes);

export const trackEmailCopyClickEvent = (attributes = {}) =>
  trackEvent(EVENT_NAME_CANDIDATE_DETAILS_EMAIL_COPY_PRESSED, attributes);

export const trackPhoneCopyClickEvent = (attributes = {}) =>
  trackEvent(EVENT_NAME_CANDIDATE_DETAILS_PHONE_COPY_PRESSED, attributes);

export const trackSortOptionChangedEvent = (attributes = {}) =>
  trackEvent(EVENT_NAME_CANDIDATE_LIST_SORT_OPTION_PRESSED, attributes);

export const trackJobInfoClickEvent = (
  eventName: string,
  seekListingId?: number,
) => {
  trackEvent(eventName, { seekListingId });
};

// tracking notes
export const trackDeletedNote = (attributes = {}) =>
  trackEvent(EVENT_NAME_DELETE_NOTE_PRESSED, attributes);
export const trackClickedNotesTab = (
  attributes = {},
  extraAttributes?: Record<string, unknown>,
) =>
  trackEvent(EVENT_NAME_NOTES_TAB_PRESSED, {
    ...attributes,
    ...extraAttributes,
  });
export const trackAddedNote = (attributes = {}) =>
  trackEvent(EVENT_NAME_ADD_NOTE_PRESSED, attributes);
export const trackClickedNotesInCandidateList = (attributes = {}) =>
  trackEvent(EVENT_NAME_NOTES_IN_CANDIDATE_LIST_PRESSED, attributes);

// tracking hirer uploaded attachments
export const trackAddAttachmentStart = (attributes = {}) =>
  trackEvent(EVENT_NAME_ADD_ATTACHMENT_PRESSED, attributes);
export const trackAddedAttachment = (attributes = {}) =>
  trackEvent(EVENT_NAME_ATTACHMENT_UPLOAD_SUCCEEDED, attributes);
export const trackDeleteAttachment = (attributes = {}) =>
  trackEvent(EVENT_NAME_CANDIDATE_DOCUMENTS_DELETE_PRESSED, attributes);

export const trackDesktopClickedResumeInCandidateList = (attributes = {}) =>
  trackEvent(EVENT_NAME_DESKTOP_CLICKED_RESUME_IN_CANDIDATE_LIST, attributes);
export const trackDesktopClickedCoverLetterInCandidateList = (
  attributes = {},
) =>
  trackEvent(
    EVENT_NAME_DESKTOP_CLICKED_COVER_LETTER_IN_CANDIDATE_LIST,
    attributes,
  );
export const trackDesktopClickedSelectionCriteriaInCandidateList = (
  attributes = {},
) => trackEvent(EVENT_NAME_DESKTOP_SELECTION_CRITERIA_LIST_PRESSED, attributes);

// tracking emails
export const trackSentEmailSingle = (attributes: SentEmailSingleAttributes) => {
  trackEvent(EVENT_NAME_SEND_EMAIL_SUCCEEDED, attributes);
};
export const trackSentEmailMultiple = (
  attributes: SentEmailMultipleAttributes,
) => {
  trackEvent(EVENT_NAME_BULK_ACTIONS_SEND_EMAIL_SUCCEEDED, attributes);
};
export const trackUpdateStatusMultiple = (
  attributes: UpdateStatusMultipleAttributes,
) => {
  trackEvent(EVENT_NAME_BULK_ACTIONS_CHANGE_STATUS_SUCCEEDED, attributes);
};
export const trackClickedEmailsTab = (attributes = {}) =>
  trackEvent(EVENT_NAME_EMAIL_TAB_PRESSED, attributes);
export const trackClickedSendEmail = (attributes = {}) =>
  trackEvent(EVENT_NAME_SEND_EMAIL_PRESSED, attributes);
export const trackClickedEmailsInCandidateList = (attributes = {}) =>
  trackEvent(EVENT_NAME_EMAIL_CANDIDATE_LIST_PRESSED, attributes);

// tracking profile
export const trackClickedProfileTab = (attributes = {}) =>
  trackEvent(EVENT_NAME_PROFILE_TAB_PRESSED, attributes);

export const trackClickedMoreQuestions = (attributes = {}) =>
  trackEvent(EVENT_NAME_MORE_QUESTIONS_CANDIDATE_LIST_PRESSED, attributes);

// tracking profile
export const trackClickedPreviousApplicationsTab = (attributes = {}) =>
  trackEvent(EVENT_NAME_CLICKED_PREVIOUS_APPLICATIONS_TAB, attributes);

// tracking prospect rating
export const trackChangeRating = (attributes = {}) => {
  trackEvent(EVENT_NAME_CHANGED_RATING, attributes);
};

export const trackClearedRating = (attributes = {}) => {
  trackEvent(EVENT_NAME_CLEARED_RATING, attributes);
};

export const trackClickedRatingsInCandidateList = (attributes = {}) =>
  trackEvent(EVENT_NAME_RATING_IN_CANDIDATE_LIST_PRESSED, attributes);

// tracking bulk actions
export const trackBulkActionsMoveTabClicked = (
  attributes: TrackBulkActionsItemLinkClickedAttributes,
) => {
  trackEvent(EVENT_NAME_BULK_ACTIONS_MOVE_TAB_PRESSED, attributes);
};
export const trackBulkActionsEmailTabClicked = (
  attributes: TrackBulkActionsItemLinkClickedAttributes,
) => {
  trackEvent(EVENT_NAME_BULK_ACTIONS_EMAIL_TAB_PRESSED, attributes);
};
export const trackViewApplicationsAfterBulkMovePressed = () => {
  trackEvent(EVENT_NAME_VIEW_APPLICATIONS_AFTER_BULK_MOVE_PRESSED, {});
};
export const trackShowMoreCandidatesInBulkActionPressed = () => {
  trackEvent(EVENT_NAME_SHOW_MORE_CANDIDATES_IN_BULK_ACTION_PRESSED, {});
};

// tracking job actions
export const trackViewAdClickEvent = (attributes = {}) =>
  trackEvent(EVENT_NAME_JOB_ACTIONS_VIEW_AD_PRESSED, attributes);
export const trackEditAdClickEvent = (attributes = {}) =>
  trackEvent(EVENT_NAME_JOB_ACTIONS_EDIT_AD_PRESSED, attributes);
export const trackExportToCsvClickEvent = (attributes = {}) =>
  trackEvent(EVENT_NAME_DOWNLOAD_CANDIDATES_LINK_PRESSED, attributes);
export const trackViewJobAdInformationClickEvent = (attributes = {}) =>
  trackEvent(EVENT_NAME_JOB_ACTIONS_VIEW_AD_INFORMATION_PRESSED, attributes);
export const trackRefreshAdClickEvent = (attributes = {}) =>
  trackEvent(EVENT_NAME_JOB_ACTIONS_REFRESH_PRESSED, attributes);
export const trackCopyAdClickEvent = (attributes = {}) =>
  trackEvent(EVENT_NAME_JOB_ACTIONS_COPY_PRESSED, attributes);
export const trackExpireAdClickEvent = (attributes = {}) =>
  trackEvent(EVENT_NAME_JOB_ACTIONS_EXPIRE_PRESSED, attributes);
export const trackViewJobNotificationsClickEvent = (attributes = {}) =>
  trackEvent(EVENT_NAME_JOB_ACTIONS_NOTIFICATIONS_SELF_PRESSED, attributes);

export const trackViewTeamAccessClickEvent = (attributes = {}) =>
  trackEvent(EVENT_JOB_ACTIONS_TEAM_ACCESS_PRESSED, attributes);

export const trackViewAdPerformanceLinkClickEvent = (attributes = {}) =>
  trackEvent(EVENT_NAME_VIEW_AD_PERFORMANCE_PRESSED, attributes);

// share job ad in social media
export const trackShareJobOnFacebookEvent = (attributes = {}) =>
  trackEvent(EVENT_NAME_SOCIAL_SHARE_FACEBOOK_PRESSED, attributes);
export const trackShareJobOnTwitterEvent = (attributes = {}) =>
  trackEvent(EVENT_NAME_SOCIAL_SHARE_TWITTER_PRESSED, attributes);
export const trackShareJobOnLinkedInEvent = (attributes = {}) =>
  trackEvent(EVENT_NAME_SOCIAL_SHARE_LINKEDIN_PRESSED, attributes);
export const trackShareCopyJobAdEvent = (attributes = {}) =>
  trackEvent(EVENT_NAME_SOCIAL_SHARE_COPY_JOBAD_PRESSED, attributes);

// tracking talent search / Recommended Candidates
export const trackTalentSearchLinkOutClickEvent = (attributes = {}) =>
  trackEvent(EVENT_TALENT_SEARCH_BUTTON_LINK_OUT_CLICKED, attributes);
export const trackCandidateManagementTalentSearchViewRecommendedCandidatesBtnPressed =
  () =>
    trackEvent(
      EVENT_CANDIDATE_MANAGEMENT_TALENT_SEARCH_VIEW_RECOMMENDED_CANDIDATES_BTN_PRESSED,
      {},
    );

export const trackBetaExperienceToggleToJdcl = () => {
  trackEvent(EVENT_NAME_TAB_OPT_OUT_PRESSED, {});
};

// tracking previous applications
export const trackClickedPreviousApplicationsInCandidateList = (
  attributes = {},
) =>
  trackEvent(
    EVENT_NAME_PREVIOUS_APPLICATIONS_IN_CANDIDATE_LIST_PRESSED,
    attributes,
  );

// tracking pagination
export type PaginationButtonType = 'number' | 'previous' | 'next';

export const trackPaginationClickEvent = (
  buttonType: PaginationButtonType,
  isLastPage: boolean,
  attributes = {},
) => {
  const getEventName = () => {
    switch (buttonType) {
      case 'previous':
        return EVENT_NAME_CLICKED_PREVIOUS_PAGE_BUTTON;
      case 'next':
        return EVENT_NAME_CLICKED_NEXT_PAGE_BUTTON;
      default:
        return EVENT_NAME_NUMBERED_PAGE_PRESSED;
    }
  };
  const eventName = getEventName();
  return trackEvent(eventName, attributes);
};

// tracking filters
export interface FiltersInteractedAttributes {
  seekAdvertiserId: string;
  userId: number;
  seekListingId: number;
  isCandidateListFiltered: boolean;
  isParentFilterSelected?: boolean;
  actionOrigin?: string;
}

// must have filters attributes
interface MustHaveFiltersAttributes {
  toggleState: typeof TOGGLE_STATE_ON | typeof TOGGLE_STATE_OFF;
  toggleType: typeof MUST_HAVE_TOGGLE_TYPE;
}

type MustHaveFiltersInteractedAttributes = FiltersInteractedAttributes &
  MustHaveFiltersAttributes;

interface SRNBannerAttributes {
  linkName?: string;
  eventName?: string;
  bannerElement?: string;
  alertShownMessageCode?: string;
  currentPage?: typeof TRACKING_CANDIDATE_LIST;
}

export const trackFiltersInteracted = (
  eventName:
    | typeof EVENT_NAME_FILTERS_INTERACTED
    | typeof EVENT_NAME_CLEAR_FILTERS_BUTTON_PRESSED,
  attributes: FiltersInteractedAttributes,
) => {
  trackEvent(eventName, attributes);
};

export const trackParentFilterInteractedOn = (
  attributes: FiltersInteractedAttributes,
) => {
  trackEvent(EVENT_NAME_PARENT_FILTER_INTERACTED_ON, attributes);
};

export const trackParentFilterInteractedOff = (
  attributes: FiltersInteractedAttributes,
) => {
  trackEvent(EVENT_NAME_PARENT_FILTER_OFF_PRESSED, attributes);
};

// Show more Matched Qualities
export const trackMatchedQualitiesViewMore = (attributes = {}) => {
  trackEvent(EVENT_NAME_MATCHED_QUALITIES_VIEW_MORE_PRESSED, attributes);
};

export const trackMatchedQualitiesViewLess = (attributes = {}) => {
  trackEvent(EVENT_NAME_MATCHED_QUALITIES_VIEW_LESS_PRESSED, attributes);
};

// Must Have
export const trackMustHaveFilterInteracted = (
  attributes: MustHaveFiltersInteractedAttributes,
) => {
  trackEvent(EVENT_NAME_MUST_HAVE_FILTER_INTERACTED, attributes);
};

// track job tabs
export const trackClickCandidateListTab = (attributes = {}) => {
  trackEvent(EVENT_NAME_CANDIDATE_LIST_TAB_PRESSED, attributes);
};

export const trackClickRecommendedCandidatesTab = (attributes = {}) => {
  trackEvent(EVENT_NAME_RECOMMENDED_CANDIDATES_TAB_PRESSED, attributes);
};
// TODO: confirm the finalized tracking name with analyst

export const trackClickHirerActivityListTab = (attributes = {}) => {
  trackEvent(EVENT_NAME_CLICKED_HIRER_ACTIVITY_LIST_TAB, attributes);
};

// track hirer activity
export const trackClickHirerActivityNoteItem = (attributes = {}) => {
  trackEvent(EVENT_NAME_ACTIVITY_LOG_NOTE_PRESSED, attributes);
};

export const trackClickHirerActivityLoadMore = (attributes = {}) => {
  trackEvent(EVENT_NAME_ACTIVITY_LOG_LOAD_MORE_PRESSED, attributes);
};

export const trackClickHirerActivityRefresh = (attributes = {}) => {
  trackEvent(EVENT_NAME_ACTIVITY_LOG_REFRESH_TO_VIEW_PRESSED, attributes);
};

// track billable applicants model
export const trackClickBillableApplicantsDefinition = (attributes: {
  actionOrigin:
    | 'Candidate Listing - Billable Applicants Button'
    | 'Candidate Listing - Billable Alert';
}) => {
  trackEvent(EVENT_NAME_PPA_BILLABLE_APPLICANTS_DEFINITION_PRESSED, attributes);
};

// track candidate card status
export const trackCandidateCardStatus = (attributes: {
  candidateCardStatus: 'Expand' | 'Collapse';
  actionOrigin:
    | 'Candidate Listing - Listing Change'
    | 'Candidate Listing - Individual Card Change';
}) => {
  trackEvent(EVENT_NAME_CANDIDATE_CARD_SIZE_CHANGE_PRESSED, attributes);
};

// track seek recruiter network banner
export const trackRecruiterNetworkBannerDisplay = (
  attributes: SRNBannerAttributes,
) => {
  trackEventView(BANNER_DISPLAYED, attributes);
};

export const trackRecruiterNetworkBannerDismissEvent = (
  attributes: SRNBannerAttributes,
) => {
  trackEvent(EVENT_NAME_ALERT_DISMISS_PRESSED, {
    eventName: EVENT_NAME_ALERT_DISMISS_PRESSED,
    alertShownMessageCode:
      'Candidate Management - SEEK Recruiter Network Banner',
    ...attributes,
  });
};

export const trackRecruiterNetworkBannerButtonPressedEvent = (
  attributes: SRNBannerAttributes,
) => {
  trackEvent(EVENT_NAME_SRN_BUTTON_PRESSED, {
    linkName: 'banner_content_pressed',
    eventName: 'banner_content_pressed',
    ...attributes,
  });
};

export const trackFreemiumButtonPressedEvent = () => {
  trackEvent(EVENT_NAME_FREEMIUM_BUTTON_PRESSED, {
    linkName: 'job_upgrade_button_pressed',
    eventName: 'job_upgrade_button_pressed',
  });
};

export const trackFreemiumButtonDisplay = (attributes: {
  hasJobUpgradeButton: boolean;
}) => {
  trackEvent(EVENT_NAME_FREEMIUM_BUTTON_DISPLAYED, {
    linkName: 'job_upgrade_button_viewed',
    eventName: 'job_upgrade_button_viewed',
    ...attributes,
  });
};
